import { useCallback, useEffect, useRef, useState } from 'react';

import { Paged } from '~/api-services/AIStrategyBuilderService/types';

type PagedResponse<T> = GenericAPIResponse & { returnData: Paged<T> };

type Fetcher<T> = (params: { page: number; size: number }) => Promise<{ data: PagedResponse<T> }>;

const extractError = (err: unknown): string => {
    const anyErr = err as any;
    return anyErr?.response?.data?.message || anyErr?.response?.data?.errorCode || anyErr?.message || 'Unknown error';
};

// `fetcher` must be stable (useCallback in the caller) — an inline arrow refetches every render.
// Responses are matched on a request id so an older refresh losing a race cannot overwrite a
// newer one.
export const useStrategyPaged = <T>(fetcher: Fetcher<T>, pageSize = 20) => {
    const [items, setItems] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const requestIdRef = useRef(0);

    const fetchPage = useCallback(
        async (pageToLoad: number, append: boolean) => {
            const requestId = ++requestIdRef.current;
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setError(null);
            }
            try {
                const { data } = await fetcher({ page: pageToLoad, size: pageSize });
                if (requestId !== requestIdRef.current) {
                    return;
                }
                if (data?.status && data?.returnData) {
                    const paged = data.returnData;
                    setItems((prev) => (append ? [...prev, ...(paged.content || [])] : paged.content || []));
                    setPage(paged.page ?? pageToLoad);
                    setTotalPages(paged.totalPages ?? 0);
                } else {
                    setError(data?.errorCode || 'Failed to load');
                    if (!append) {
                        setItems([]);
                    }
                }
            } catch (err) {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                setError(extractError(err));
                if (!append) {
                    setItems([]);
                }
            } finally {
                if (requestId === requestIdRef.current) {
                    if (append) {
                        setLoadingMore(false);
                    } else {
                        setLoading(false);
                    }
                }
            }
        },
        [fetcher, pageSize]
    );

    const refresh = useCallback(() => fetchPage(0, false), [fetchPage]);

    const loadMore = useCallback(() => {
        if (loading || loadingMore || page >= totalPages - 1) {
            return;
        }
        fetchPage(page + 1, true);
    }, [fetchPage, loading, loadingMore, page, totalPages]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const exhausted = totalPages === 0 || page >= totalPages - 1;

    return { items, loading, loadingMore, error, refresh, loadMore, exhausted };
};
